<template>
  <div v-if="value" class="inspection-step-dialog-overlay" @click.self="handleOverlayClick">
    <div class="inspection-step-dialog">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <img :src="require('@components/assets/search-refraction.svg')" alt="inspection" class="header-icon-img" />
          <h2 class="header-title">Inspection Step</h2>
        </div>
        <button class="close-button" @click="handleClose" aria-label="Close dialog">
          <img :src="require('@components/assets/x-close.svg')" alt="close" />
        </button>
      </div>

      <!-- Dialog Content -->
      <div class="dialog-content">
        <!-- Action Bar -->
        <div class="action-bar">
          <div class="action-bar-row">
            <!-- Inspect Label and Category Picker -->
            <div class="inspect-section">
              <span class="inspect-label">Inspect</span>
              <CompetenceCategoryPicker
                :modelValue="localCategory"
                @update:modelValue="handleCategoryChange"
              />
            </div>

            <!-- Spacer -->
            <div class="action-spacer"></div>

            <!-- Buttons -->
            <div class="action-buttons">
              <StandardButton
                type="magenta"
                variant="outline"
                size="normal"
                label="Discard"
                @click="handleDiscard"
              />
              <StandardButton
                type="magenta"
                variant="primary"
                size="normal"
                :label="isEditMode ? 'Save' : 'Add'"
                :left-icon="isEditMode ? undefined : 'plusLeft'"
                :disabled="!canSubmit"
                @click="handleSubmit"
              />
            </div>
          </div>
        </div>

        <!-- Form Fields -->
        <div class="form-fields">
          <div class="form-field">
            <ExTextInputWithLimit
              :value="localInspect"
              label="Inspect"
              :placeholder="'What do you want to inspect'"
              :ghost-text="'What do you want to inspect'"
              :status="getFieldStatus('inspect')"
              :min="3"
              :max="120"
              :show-char-counter="false"
              @input="handleInspectChange"
            />
          </div>

          <div class="form-field">
            <ExTextInputWithLimit
              :value="localLookFor"
              label="Look for"
              :placeholder="'What are you looking for'"
              :ghost-text="'What are you looking for'"
              :status="getFieldStatus('lookFor')"
              :min="3"
              :max="120"
              :show-char-counter="false"
              @input="handleLookForChange"
            />
          </div>

          <div class="form-field">
            <ExTextInputWithLimit
              :value="localPassIf"
              label="Pass Inspection if"
              :placeholder="'Describe pass'"
              :ghost-text="'Describe pass'"
              :status="getFieldStatus('passIf')"
              :min="3"
              :max="120"
              :show-char-counter="false"
              @input="handlePassIfChange"
            />
          </div>

          <div class="form-field">
            <ExTextInputWithLimit
              :value="localFailIf"
              label="Fail Inspection if"
              :placeholder="'Describe failure'"
              :ghost-text="'Describe failure'"
              :status="getFieldStatus('failIf')"
              :min="3"
              :max="120"
              :show-char-counter="false"
              @input="handleFailIfChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import CompetenceCategoryPicker from '@components/CompetenceCategoryPicker/CompetenceCategoryPicker.vue';
import ExTextInputWithLimit from '@components/TextInputWithLimit/ExTextInputWithLimit.vue';
import StandardButton from '@components/StandardButton/StandardButton.vue';

const props = defineProps({
  /**
   * Controls dialog visibility (v-model)
   */
  value: {
    type: Boolean,
    default: false
  },
  /**
   * Dialog state: Empty (new), Add (filled new), Edit (existing unchanged), Save (existing changed)
   */
  state: {
    type: String,
    default: 'Empty',
    validator: (value) => ['Empty', 'Add', 'Edit', 'Save'].includes(value)
  },
  /**
   * Initial category value
   */
  category: {
    type: String,
    default: 'knowledge',
    validator: (value) => ['knowledge', 'ability', 'behaviour'].includes(value)
  },
  /**
   * Initial inspect value
   */
  inspect: {
    type: String,
    default: ''
  },
  /**
   * Initial look for value
   */
  lookFor: {
    type: String,
    default: ''
  },
  /**
   * Initial pass if value
   */
  passIf: {
    type: String,
    default: ''
  },
  /**
   * Initial fail if value
   */
  failIf: {
    type: String,
    default: ''
  },
  /**
   * Whether to close on overlay click
   */
  persistent: {
    type: Boolean,
    default: false
  },
  /**
   * Whether to call API on save (if false, just emit submit event)
   */
  callApiOnSave: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'input',
  'close',
  'discard',
  'submit',
  'change'
]);

// Local state
const localCategory = ref(props.category);
const localInspect = ref(props.inspect);
const localLookFor = ref(props.lookFor);
const localPassIf = ref(props.passIf);
const localFailIf = ref(props.failIf);

// Track original values for dirty checking
const originalValues = ref({
  category: props.category,
  inspect: props.inspect,
  lookFor: props.lookFor,
  passIf: props.passIf,
  failIf: props.failIf
});

// Computed properties
const isDirty = computed(() => {
  return (
    localCategory.value !== originalValues.value.category ||
    localInspect.value !== originalValues.value.inspect ||
    localLookFor.value !== originalValues.value.lookFor ||
    localPassIf.value !== originalValues.value.passIf ||
    localFailIf.value !== originalValues.value.failIf
  );
});

const isFormValid = computed(() => {
  const inspectValid = localInspect.value && localInspect.value.trim().length >= 3;
  const lookForValid = localLookFor.value && localLookFor.value.trim().length >= 3;
  const passIfValid = localPassIf.value && localPassIf.value.trim().length >= 3;
  const failIfValid = localFailIf.value && localFailIf.value.trim().length >= 3;
  
  return inspectValid && lookForValid && passIfValid && failIfValid;
});

// Check if we're in edit mode (editing existing data)
const isEditMode = computed(() => {
  return props.state === 'Edit' || props.state === 'Save';
});

// Can submit when form is valid AND has changes
const canSubmit = computed(() => {
  return isFormValid.value && isDirty.value;
});

// Methods
const getFieldStatus = (fieldName) => {
  const fieldMap = {
    inspect: { value: localInspect.value, original: originalValues.value.inspect },
    lookFor: { value: localLookFor.value, original: originalValues.value.lookFor },
    passIf: { value: localPassIf.value, original: originalValues.value.passIf },
    failIf: { value: localFailIf.value, original: originalValues.value.failIf }
  };
  
  const field = fieldMap[fieldName];
  if (!field) return 'Not focused';
  
  // Check if field has been modified from original
  if (field.value !== field.original && field.value && field.value.length > 0) {
    return 'Dirty';
  }
  
  return 'Not focused';
};

const handleInspectChange = (value) => {
  localInspect.value = value;
  emitChange();
};

const handleLookForChange = (value) => {
  localLookFor.value = value;
  emitChange();
};

const handlePassIfChange = (value) => {
  localPassIf.value = value;
  emitChange();
};

const handleFailIfChange = (value) => {
  localFailIf.value = value;
  emitChange();
};

const handleCategoryChange = (value) => {
  localCategory.value = value;
  emitChange();
};

const emitChange = () => {
  emit('change', {
    category: localCategory.value,
    inspect: localInspect.value,
    lookFor: localLookFor.value,
    passIf: localPassIf.value,
    failIf: localFailIf.value,
    isDirty: isDirty.value,
    isValid: isFormValid.value
  });
};

const handleClose = () => {
  emit('input', false);
  emit('close');
};

const handleOverlayClick = () => {
  if (!props.persistent) {
    handleClose();
  }
};

const handleDiscard = () => {
  // Reset to original values
  localCategory.value = originalValues.value.category;
  localInspect.value = originalValues.value.inspect;
  localLookFor.value = originalValues.value.lookFor;
  localPassIf.value = originalValues.value.passIf;
  localFailIf.value = originalValues.value.failIf;
  
  emit('discard');
};

const handleSubmit = () => {
  if (!isFormValid.value) return;
  
  emit('submit', {
    category: localCategory.value,
    inspect: localInspect.value,
    lookFor: localLookFor.value,
    passIf: localPassIf.value,
    failIf: localFailIf.value
  });
};

// Handle escape key
const handleKeydown = (event) => {
  if (event.key === 'Escape' && props.value && !props.persistent) {
    handleClose();
  }
};

// Watchers
watch(() => props.value, (newVal) => {
  if (newVal) {
    // Reset local state when dialog opens
    localCategory.value = props.category;
    localInspect.value = props.inspect;
    localLookFor.value = props.lookFor;
    localPassIf.value = props.passIf;
    localFailIf.value = props.failIf;
    
    // Update original values
    originalValues.value = {
      category: props.category,
      inspect: props.inspect,
      lookFor: props.lookFor,
      passIf: props.passIf,
      failIf: props.failIf
    };
    
    // Add escape key listener (remove first to prevent duplicates)
    document.removeEventListener('keydown', handleKeydown);
    document.addEventListener('keydown', handleKeydown);
  } else {
    document.removeEventListener('keydown', handleKeydown);
  }
}, { immediate: true });

// Lifecycle
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.inspection-step-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(52, 64, 84, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.inspection-step-dialog {
  position: relative;
  width: 1031px;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 48px);
  background-color: #ffffff;
  background-image: url('@components/assets/backgrounds/background_popup_icon.svg');
  background-repeat: no-repeat;
  background-position: top left;
  border-radius: 12px;
  box-shadow: 
    0px 20px 24px -4px rgba(16, 24, 40, 0.08),
    0px 8px 8px -4px rgba(16, 24, 40, 0.03);
  overflow: visible;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Modal Header */
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 20px 24px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-icon-img {
  width: 48px;
  height: 48px;
}

.header-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}

.close-button:hover {
  background-color: #f9fafb;
}

.close-button img {
  width: 24px;
  height: 24px;
}

/* Dialog Content */
.dialog-content {
  display: flex;
  flex-direction: column;
}

/* Action Bar */
.action-bar {
  border: 1px solid #e4e4e4;
  padding: 19px 13px;
}

.action-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.inspect-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.inspect-label {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 30px;
  color: #475467;
}

.action-spacer {
  flex: 1;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

/* Form Fields */
.form-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 19px 13px;
  border: 1px solid #e4e4e4;
  border-top: none;
}

.form-field {
  width: 100%;
  margin-top: 10px;
}

/* Override ExTextInputWithLimit label styles for this dialog */
.form-field :deep(.main_label) {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}
</style>

